import React, { useEffect, useState } from 'react';
import { Shell } from '../../components/layout/Shell';
import { api } from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { toast } from 'sonner';
import { Globe, Buildings, DownloadSimple, MagnifyingGlass } from '@phosphor-icons/react';

const Discover = () => {
  const [presets, setPresets] = useState([]);
  const [importing, setImporting] = useState(false);
  const [results, setResults] = useState(null);

  const [query, setQuery] = useState('customer support');
  const [rf, setRf] = useState('admin-ops-support');
  const [limit, setLimit] = useState(20);
  const [searching, setSearching] = useState(false);
  const [searchResult, setSearchResult] = useState(null);

  const [families, setFamilies] = useState([]);

  useEffect(() => {
    api.get('/discover/ats/presets').then((r) => setPresets(r.data));
    api.get('/role-families').then((r) => setFamilies(r.data));
  }, []);

  const importAll = async () => {
    setImporting(true);
    try {
      const { data } = await api.post('/discover/ats/import-all');
      const totals = Object.entries(data).reduce((acc, [, v]) => acc + (v.added || 0), 0);
      toast.success(`Imported ${totals} jobs from ${Object.keys(data).length} employers`);
      setResults(data);
    } catch {
      toast.error('Import failed');
    } finally { setImporting(false); }
  };

  const runSearch = async () => {
    if (!query.trim()) return;
    setSearching(true);
    try {
      const { data } = await api.post('/discover/search', {
        query, limit: Number(limit), role_family_slug: rf,
      });
      toast.success(`Found ${data.found}, added ${data.added} · skipped ${data.skipped}`);
      setSearchResult(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Search failed');
    } finally { setSearching(false); }
  };

  return (
    <Shell>
      <div className="max-w-[1200px] mx-auto px-6 py-10">
        <div className="label-eyebrow text-primary mb-2">ADMIN · DISCOVER</div>
        <h1 className="font-display font-black text-4xl tracking-tighter mb-3">Bring jobs in from the open web.</h1>
        <p className="text-sm text-muted-foreground max-w-2xl leading-relaxed mb-8">
          No partnership wait — populate Hikari today from public employer ATS boards (Canadian preset) and
          from a global remote-jobs web search.
        </p>

        {/* Canadian ATS preset */}
        <section className="border border-border bg-white p-8 mb-8" data-testid="canada-ats-section">
          <div className="flex items-center gap-2 mb-4">
            <Buildings size={22} weight="duotone" className="text-primary" />
            <h2 className="font-display font-black text-xl tracking-tight">Canadian employers · public ATS</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-4">
            A curated list of well-known Canadian employers with open Greenhouse / Lever boards.
            One click imports everything currently listed — idempotent.
          </p>
          <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-0 grid-skeleton mb-6">
            {presets.map((p) => (
              <div key={p.name} className="p-3 bg-white text-center" data-testid={`preset-${p.name.toLowerCase()}`}>
                <div className="font-display font-black text-sm tracking-tight">{p.name}</div>
                <div className="mono text-[10px] text-muted-foreground mt-1 truncate">
                  {p.url.replace(/^https:\/\//, '')}
                </div>
              </div>
            ))}
          </div>
          <Button onClick={importAll} disabled={importing} className="rounded-none" data-testid="import-all-ats">
            <DownloadSimple size={16} className="mr-2" /> {importing ? 'Importing...' : 'Import all presets'}
          </Button>
          {results && (
            <div className="mt-5 border-t border-border pt-4">
              <div className="label-eyebrow text-muted-foreground mb-2">IMPORT RESULTS</div>
              <div className="grid sm:grid-cols-2 md:grid-cols-5 gap-2 text-xs mono">
                {Object.entries(results).map(([name, r]) => (
                  <div key={name} className="border border-border p-2" data-testid={`result-${name.toLowerCase()}`}>
                    <div className="font-bold">{name}</div>
                    <div>+{r.added || 0} · skip {r.skipped || 0}</div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </section>

        {/* Web search */}
        <section className="border border-border bg-white p-8" data-testid="web-search-section">
          <div className="flex items-center gap-2 mb-4">
            <Globe size={22} weight="duotone" className="text-primary" />
            <h2 className="font-display font-black text-xl tracking-tight">Search the web for jobs</h2>
          </div>
          <p className="text-sm text-muted-foreground mb-5">
            Pulls from the Remotive global jobs feed (remote + regional). Good for testing assessment
            fit against a wider pool of real postings.
          </p>
          <div className="grid md:grid-cols-12 gap-2">
            <div className="md:col-span-5">
              <Input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="e.g. customer support, operations coordinator"
                className="rounded-none h-11"
                data-testid="search-query"
              />
            </div>
            <div className="md:col-span-3">
              <Select value={rf} onValueChange={setRf}>
                <SelectTrigger className="rounded-none h-11" data-testid="search-rf"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {families.map((f) => <SelectItem key={f.slug} value={f.slug}>{f.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="md:col-span-2">
              <Input type="number" value={limit} onChange={(e) => setLimit(e.target.value)} className="rounded-none h-11" data-testid="search-limit" min={1} max={50} />
            </div>
            <div className="md:col-span-2">
              <Button onClick={runSearch} disabled={searching || !query.trim()} className="rounded-none w-full h-11" data-testid="search-run">
                <MagnifyingGlass size={16} className="mr-2" /> {searching ? '...' : 'Search'}
              </Button>
            </div>
          </div>
          {searchResult && (
            <div className="mt-5 mono text-xs text-muted-foreground">
              Found <b>{searchResult.found}</b> · Added <b>{searchResult.added}</b> · Skipped <b>{searchResult.skipped}</b>
            </div>
          )}
        </section>
      </div>
    </Shell>
  );
};

export default Discover;
